import "./polyfills"
import "./js/sentry"
import "./js/plausible"
import * as Sentry from "@sentry/react"
import "./index.css"
import "./i18n.config"
import React from "react"
import { createRoot } from "react-dom/client"
import { createRoutesFromElements, Route, RouterProvider } from "react-router"
import { LayoutContext } from "./context"
import { useEthereum } from "./state/hooks/useEthereum"
import { ErrorTemplate } from "./components/templates/Error"
import { Domain } from "./domain"
import { PwaRefresh } from "@cividao/components"

import { Component as RootPage } from "./pages/Root"
import { Component as AuthProtectedRoutes } from "./pages/AuthProtectedRoutes"
import { Component as KycVerifiedRoutes } from "./pages/KycVerifiedRoutes"
import { Component as WalletProtectedRoutes } from "./pages/WalletProtectedRoutes"
import { Component as ErrorPage } from "./pages/Error"
import { Component as WelcomePage } from "./pages/Welcome"
import { Component as LoginPage } from "./pages/Login"
import { Component as RegisterPage } from "./pages/Register"

import { loader as rootLoader } from "./pages/Root/loader"
import { loader as authProtectedRoutesLoader } from "./pages/AuthProtectedRoutes/loader"
import { loader as kycVerifiedRoutesLoader } from "./pages/KycVerifiedRoutes/loader"
import { loader as walletProtectedRoutesLoader } from "./pages/WalletProtectedRoutes/loader"
import { loader as kycLoader } from "./pages/Kyc/loader"
import { loader as createWalletLoader } from "./pages/CreateWallet/loader"
import { loader as connectWalletLoader } from "./pages/ConnectWallet/loader"
import { loader as welcomeLoader } from "./pages/Welcome/loader"
import { loader as loginLoader } from "./pages/Login/loader"
import { loader as registerLoader } from "./pages/Register/loader"

import { sentryCreateBrowserRouter } from "./js/sentry"

window.domain =
  window.domain ??
  Domain.create({
    CHAIN_ID: import.meta.env.VITE_CHAIN_ID,
    GRAPHQL_URI: import.meta.env.VITE_GRAPHQL_URI,
    HW_PROJECT_ID: import.meta.env.VITE_HW_PROJECT_ID,
    MIGRATION_GRAPHQL_URI: import.meta.env.VITE_MIGRATION_GRAPHQL_URI,
    VITE_OPENSEA_URL: import.meta.env.VITE_OPENSEA_URL,
  })

const router = sentryCreateBrowserRouter(
  createRoutesFromElements(
    <>
      <Route id="root" loader={rootLoader} element={<RootPage />} errorElement={<ErrorTemplate />}>
        <Route index element={<WelcomePage />} loader={welcomeLoader} />
        <Route path="login" element={<LoginPage />} loader={loginLoader} />
        <Route path="register" element={<RegisterPage />} loader={registerLoader} />
        <Route path="terms" lazy={async () => import("./pages/Terms")} />
        <Route path="privacy" lazy={async () => import("./pages/Privacy")} />
        <Route path="faq" lazy={async () => import("./pages/Faq")} />

        <Route element={<AuthProtectedRoutes />} loader={authProtectedRoutesLoader} shouldRevalidate={() => true}>
          <Route path="kyc" loader={kycLoader} lazy={async () => import("./pages/Kyc")} />
          <Route path="get-ine" lazy={async () => import("./pages/GetIne")} />

          <Route element={<WalletProtectedRoutes />}>
            <Route path="vote/:votingId" lazy={async () => import("./pages/VoteDetail")} />
          </Route>

          <Route element={<KycVerifiedRoutes />} loader={kycVerifiedRoutesLoader}>
            <Route path="create-wallet" loader={createWalletLoader} lazy={async () => import("./pages/CreateWallet")} />
            <Route
              path="connect-wallet"
              loader={connectWalletLoader}
              lazy={async () => import("./pages/ConnectWallet")}
            />

            <Route element={<WalletProtectedRoutes />} loader={walletProtectedRoutesLoader}>
              <Route path="participation-preference" lazy={async () => import("./pages/ParticipationPreference")} />
              <Route path="dashboard" lazy={async () => import("./pages/Dashboard")} />
              <Route path="my-votings" lazy={async () => import("./pages/MyVotings")} />
              <Route path="profile" lazy={async () => import("./pages/Profile")} />
              <Route path="personal-data" lazy={async () => import("./pages/PersonalData")} />
              <Route path="user" lazy={async () => import("./pages/User")} />
              <Route path="subscription" lazy={async () => import("./pages/Subscription")} />
              <Route path="wallet" lazy={async () => import("./pages/Wallet")} />
            </Route>
            <Route path="notifications" lazy={async () => import("./pages/Notifications")} />
            <Route path="notification-preferences" lazy={async () => import("./pages/NotificationPreferences")} />
          </Route>
        </Route>
        <Route path="*" element={<ErrorPage />} />
      </Route>
    </>,
  ),
)

const App = () => {
  useEthereum()

  return (
    <React.StrictMode>
      <LayoutContext>
        <PwaRefresh>
          <RouterProvider router={router} />
        </PwaRefresh>
      </LayoutContext>
    </React.StrictMode>
  )
}

const container = document.getElementById("root")!
const root = createRoot(container, {
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn("Uncaught error", error, errorInfo.componentStack)
  }),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
})
root.render(<App />)
